import React from 'react';
import styles from './news_list.module.scss';
import { Article, Author } from '../../../news_search_react/types/news_search.types';
import { dateToString } from '../../../utilities/utilities';
import { NEWS_PATH } from '../../../news_search_react/constants/news_search.constants';
import ErrorBoundary from '../../../components/react/error_boundary/error_boundary';
import {
  EVENT_NAME_PROP,
  NEWS_SECTION_PROP,
  VIEW_SINGLE_RESULT_INTENTION,
} from '../../constants/global_search.constants';
import classNames from 'classnames';
const CLOUDINARY_BASE_PATH =
  'https://res.cloudinary.com/devex/image/fetch/c_fill,f_auto,q_auto,w_416,h_234';
const TRACKING = [EVENT_NAME_PROP, VIEW_SINGLE_RESULT_INTENTION, NEWS_SECTION_PROP].join(' ');

interface Props {
  results: Article[];
  isFetching: boolean;
}

const NewsList: React.FC<Props> = ({ results, isFetching }) => {
  const renderSingleNewsPlaceholder = (
    <>
      <div className='col-md-4 col-12 my-md-0 my-15'>
        <div className='placeholder-glow'>
          <p className={`placeholder w-100 ${styles.imgPlaceholder}`}></p>
        </div>
      </div>
      <div className='col-md-8 col-12 mt-md-5 mb-md-0 my-15'>
        <div className='placeholder-glow'>
          <p className='placeholder w-100 placeholder-lg d-xl-block d-none' />
          <p className='placeholder w-75' />
          <p className='placeholder w-100 placeholder-lg' />
          <p className='placeholder w-75 placeholder-lg' />
          <p className='placeholder w-50 placeholder-lg' />
        </div>
      </div>
    </>
  );
  const renderLoadingPlaceholder = (
    <div data-testid='loading-placeholder' className='row'>
      {renderSingleNewsPlaceholder}
      {renderSingleNewsPlaceholder}
      {renderSingleNewsPlaceholder}
    </div>
  );

  const renderResults = (
    <div data-testid='news-list'>
      {results.slice(0, 3).map(
        (article: Article): JSX.Element => (
          <ErrorBoundary key={article.id}>
            <div className='row py-35 border-bottom'>
              <div className='col-sm-4 d-none d-md-block'>
                {article.thumbnail && (
                  <div className='relative'>
                    {article.overline && (
                      <div className={`${styles.overlayedLabelForOverline} text-end`}>
                        <span className='text-white'>{article.overline}</span>
                      </div>
                    )}
                    <figure>
                      <img
                        src={`${CLOUDINARY_BASE_PATH}/${article.thumbnail}`}
                        className='w-100'
                        alt={`${article.title} thumbnail`}
                      />
                    </figure>
                  </div>
                )}
              </div>
              <div className='col-sm-8 col-12'>
                {article.overline && (
                  <div className={`d-md-none pb-2 ${styles.mobileOverlineLink}`}>
                    <span className='fs-4 text-orange-devex'>{article.overline}</span>
                  </div>
                )}
                <a
                  className={classNames('link-gray-blue', TRACKING, styles.articleName)}
                  href={`${NEWS_PATH}/${article.slug_and_id}`}
                >
                  {article.title}
                </a>
                <div className={`py-25 ${styles.articleAuthor}`}>
                  {!!article.authors.length && (
                    <>
                      <span>
                        By{' '}
                        {article.authors.map((author: Author, idx: number) => (
                          <span key={idx}>
                            {author.full_name && (
                              <span className={`fw-bold ${styles.authorLink}`}>
                                {author.full_name}
                              </span>
                            )}
                            {idx + 1 < article.authors.length && ', '}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                  {article.publication_date && !article.is_evergreen && (
                    <>
                      {!!article.authors.length && <span className='px-2 small'>{'//'}</span>}
                      <span>{dateToString(new Date(article.publication_date), false)}</span>
                    </>
                  )}
                </div>
                <p className='mb-1 devex-font-family-serif'>{article.teaser}</p>
              </div>
            </div>
          </ErrorBoundary>
        )
      )}
    </div>
  );

  return isFetching ? renderLoadingPlaceholder : renderResults;
};

export default NewsList;
